import { apiDelete, apiGet, apiPatch, apiPost, type QueryRecord } from './http-client';

import type { BookingResponse } from './bookings';

export type AdminClientListItem = {
  id: number;
  email: string;
  full_name: string | null;
  phone: string | null;
};

export type AdminClientListResult = {
  items: AdminClientListItem[];
  total: number;
};

export type RepresentedMinorListItem = {
  id: number;
  client_user_id?: number;
  /** Имя ребёнка: одно или два слова. */
  name: string;
  date_of_birth: string | null;
};

/** GET /api/v1/admin/clients/{id}/minors — ответ `{items,total}` (принимаем и legacy-массив). */
export async function listClientMinors(accessToken: string, clientId: number): Promise<RepresentedMinorListItem[]> {
  const raw = await apiGet<unknown>(`/api/v1/admin/clients/${clientId}/minors`, undefined, {
    accessToken
  });
  const items =
    raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : Array.isArray(raw)
        ? raw
        : [];
  const out: RepresentedMinorListItem[] = [];
  for (const row of items) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const r = row as Record<string, unknown>;
    if (typeof r.id !== 'number' || typeof r.name !== 'string') continue;
    out.push({
      id: r.id,
      client_user_id: typeof r.client_user_id === 'number' ? r.client_user_id : undefined,
      name: r.name,
      date_of_birth: typeof r.date_of_birth === 'string' ? r.date_of_birth : null
    });
  }
  return out;
}

export type ClientMinorCreateBody = {
  name: string;
  date_of_birth: string;
  /** Подтверждение сотрудника: законный представитель подписал согласие на обработку данных ребёнка. */
  consent_confirmed: boolean;
};

/** POST /api/v1/admin/clients/{client_id}/minors */
export function createClientMinor(
  accessToken: string,
  clientId: number,
  body: ClientMinorCreateBody
): Promise<RepresentedMinorListItem> {
  return apiPost<RepresentedMinorListItem>(`/api/v1/admin/clients/${clientId}/minors`, body, { accessToken });
}

export type ClientMinorPatchBody = {
  name?: string;
  date_of_birth?: string;
};

/** Тренер ищет клиента только осознанным запросом: минимум 2 символа, не больше 20 строк. */
export const TRAINER_CLIENT_SEARCH_MIN_CHARS = 2;
export const TRAINER_CLIENT_SEARCH_LIMIT = 20;

export function isTrainerClientSearchReady(query: string): boolean {
  return query.trim().length >= TRAINER_CLIENT_SEARCH_MIN_CHARS;
}

/** GET /api/v1/admin/clients — клиенты организации (тренер/админ); тренеру — только поиск. */
export function listAdminClients(
  accessToken: string,
  params?: { limit?: number; offset?: number; q?: string | null }
): Promise<AdminClientListResult> {
  const q: QueryRecord = {
    limit: params?.limit ?? TRAINER_CLIENT_SEARCH_LIMIT,
    offset: params?.offset ?? 0
  };
  if (params?.q) {
    q.q = params.q;
  }
  return apiGet<AdminClientListResult>('/api/v1/admin/clients', q, { accessToken });
}

export type AdminClientCreateBody = {
  email: string;
  full_name?: string | null;
  phone: string;
};

/** POST /api/v1/admin/clients */
export function createAdminClient(accessToken: string, body: AdminClientCreateBody): Promise<AdminClientListItem> {
  return apiPost<AdminClientListItem>('/api/v1/admin/clients', body, { accessToken });
}

/** PATCH /api/v1/admin/clients/{client_id}/minors/{minor_id} */
export function patchClientMinor(
  accessToken: string,
  clientId: number,
  minorId: number,
  body: ClientMinorPatchBody
): Promise<RepresentedMinorListItem> {
  return apiPatch<RepresentedMinorListItem>(`/api/v1/admin/clients/${clientId}/minors/${minorId}`, body, {
    accessToken
  });
}

/** DELETE /api/v1/admin/clients/{client_id}/minors/{minor_id} */
export function deleteClientMinor(accessToken: string, clientId: number, minorId: number): Promise<void> {
  return apiDelete<void>(`/api/v1/admin/clients/${clientId}/minors/${minorId}`, { accessToken });
}

export type CreateBookingForClientBody = {
  slot_id: number;
  client_id: number;
  comment?: string | null;
  represented_minor_id?: number | null;
};

/** POST /api/v1/bookings/for-client — запись клиента тренером/админом. */
export function createBookingForClient(
  accessToken: string,
  body: CreateBookingForClientBody
): Promise<BookingResponse> {
  return apiPost<BookingResponse>('/api/v1/bookings/for-client', body, { accessToken });
}

export type StaffBookingListItem = BookingResponse & {
  slot_date: string;
  start_time: string;
  end_time: string;
  service_name: string;
  cancellation_deadline_minutes: number | null;
  can_cancel: boolean;
  client_id: number | null;
  client_name: string | null;
  service_payment_required: boolean;
  payment_received: boolean;
  represented_minor_name: string | null;
  /** Телефон клиента — для звонка и сообщения из записи. */
  client_phone?: string | null;
  /** Ручная отметка прихода: пришёл / не пришёл; null — не отмечено. */
  attendance?: BookingAttendance | null;
  attendance_marked_at?: string | null;
  /** Первый успешный проход по QR в окне занятия (только при подключённом QR-доступе). */
  qr_entered_at?: string | null;
};

export type BookingAttendance = 'attended' | 'no_show';

export type PatchStaffBookingBody = {
  status?: string | null;
  comment?: string | null;
  represented_minor_id?: number | null;
  slot_id?: number | null;
};

/**
 * GET /api/v1/bookings/for-slot?slot_id=... — записи на слот для staff/тренера (ответ `items`+`total`).
 * Раньше запрашивался `GET /api/v1/bookings`, который отдаёт только собственные записи пользователя.
 */
export async function listSlotBookings(
  accessToken: string,
  slotId: number,
  organizationId?: number | null
): Promise<StaffBookingListItem[]> {
  const query: Record<string, number> = { slot_id: slotId };
  if (organizationId != null && organizationId > 0) query.organization_id = organizationId;
  const res = await apiGet<{ items?: StaffBookingListItem[] } | StaffBookingListItem[]>('/api/v1/bookings/for-slot', query, {
    accessToken
  });
  if (Array.isArray(res)) return res;
  return res?.items ?? [];
}

/** POST /api/v1/bookings/{id}/attendance — отметка прихода (null — снять отметку). */
export function setStaffBookingAttendance(
  accessToken: string,
  bookingId: number,
  attendance: BookingAttendance | null
): Promise<{ id: number; attendance: BookingAttendance | null }> {
  return apiPost<{ id: number; attendance: BookingAttendance | null }>(
    `/api/v1/bookings/${bookingId}/attendance`,
    { attendance },
    { accessToken }
  );
}

/** PATCH /api/v1/bookings/{id} — частичное обновление записи staff. */
export function patchStaffBooking(
  accessToken: string,
  bookingId: number,
  body: PatchStaffBookingBody
): Promise<StaffBookingListItem> {
  return apiPatch<StaffBookingListItem>(`/api/v1/bookings/${bookingId}`, body, { accessToken });
}

/** DELETE /api/v1/bookings/{id} — удалить запись клиента staff. */
export function deleteStaffBooking(accessToken: string, bookingId: number): Promise<void> {
  return apiDelete<void>(`/api/v1/bookings/${bookingId}`, { accessToken });
}
